import React, { useEffect, useState } from "react";
import {
    Grid,
    TextField,
    Switch,
    Typography,
    IconButton,
    Button,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import styled from "styled-components";

// Product options
import {PRODUCT_OPTIONS} from '../../constants/productsOptions';
const {currencies} = PRODUCT_OPTIONS;

const generateDefaultTiers = (moq, minPrice, maxPrice) => {
    const mid = Number(((minPrice + maxPrice) / 2).toFixed(2));
    return [
        { min: moq, max: moq * 2, p: maxPrice },
        { min: moq * 2 + 1, max: moq * 3, p: mid },
        { min: moq * 3 + 1, max: null, p: minPrice },
    ];
};

export default function PricingSection2({
    formData,
    setFormData,
    tieredPricing,
    setTieredPricing,
}) {
    console.log(tieredPricing);

    const [tieringEnabled, setTieringEnabled] = useState(
        Array.isArray(tieredPricing)
        // false
    );
    const [isMinPriceManual, setIsMinPriceManual] = useState(false);

    const { pricing, currency, moq, moqUnit, minPrice, maxPrice } = formData;

    /* ---------------- Base price change ---------------- */
    const handleBasePriceChange = (value) => {
        const base = Number(value || 0);

        setFormData((prev) => ({
            ...prev,
            pricing: base,
            maxPrice: tieringEnabled ? base : null,
            minPrice:
                tieringEnabled && !isMinPriceManual
                    ? Number((base / 2).toFixed(2))
                    : prev.minPrice,
        }));
    };

    /* ---------------- Tier generation ---------------- */
    useEffect(() => {
        if (!tieringEnabled || tieredPricing !== null || !moq || !minPrice || !maxPrice) {
            return;
        }

        setTieredPricing(
            generateDefaultTiers(Number(moq), Number(minPrice), Number(maxPrice))
        );
    }, [tieringEnabled, tieredPricing, moq, minPrice, maxPrice, setTieredPricing]);


    /* ---------------- Tier recalculation ---------------- */
    const recalcFromIndex = (tiers, startIndex) => {
        const updated = [...tiers];
        for (let i = startIndex; i < updated.length; i++) {
            updated[i].min = updated[i - 1].max + 1;
            updated[i].max =
                i === updated.length - 1
                    ? null
                    : updated[i].min + Number(moq) - 1;
        }
        return updated;
    };

    const updateTier = (index, field, value) => {
        let updated = [...tieredPricing];
        updated[index][field] = value;

        if (field === "max" && value !== null) {
            updated = recalcFromIndex(updated, index + 1);
        }
        setTieredPricing(updated);
    };

    /* ---------------- Add / Remove tier ---------------- */
    const addTier = () => {
        if (!tieredPricing || tieredPricing.length < 2) return;

        const updated = [...tieredPricing];
        const insertAt = updated.length - 1;
        const prev = updated[insertAt - 1];

        const newMin = prev.max + 1;
        const newMax = newMin + Number(moq) - 1;

        updated[insertAt].min = newMax + 1;

        updated.splice(insertAt, 0, {
            min: newMin,
            max: newMax,
            p: Number(((prev.p + minPrice) / 2).toFixed(2)),
        });

        setTieredPricing(updated);
    };

    const removeTier = (index) => {
        if (tieredPricing.length <= 2) return;
        let updated = tieredPricing.filter((_, i) => i !== index);
        updated = recalcFromIndex(updated, Math.max(1, index));
        setTieredPricing(updated);
    };

    /* ---------------- Enable / Disable tiering ---------------- */
    const toggleTiering = (enabled) => {
        setTieringEnabled(enabled);

        if (!enabled) {
            setTieredPricing(null);
            setFormData((prev) => ({
                ...prev,
                minPrice: null,
                maxPrice: null,
                pricing: prev.pricing,
            }));
        } else {
            setFormData((prev) => ({
                ...prev,
                maxPrice: prev.pricing,
                minPrice: Number((prev.pricing / 2).toFixed(2)),
            }));
            setIsMinPriceManual(false);
        }
    };

    return (
        <Wrapper className="bg-light p-4 border rounded-4 my-4">
            <Grid container spacing={2}>
                {/* PRICE */}
                <Grid item xs={4}>
                    <TextField
                        label={<span>Price <span className="text-danger">*</span></span>}
                        type="number"
                        fullWidth
                        value={pricing || ""}
                        onChange={(e) => handleBasePriceChange(e.target.value)}
                    />
                </Grid>

                {/* CURRENCY */}
                <Grid item xs={2}>
                    <FormControl fullWidth>
                        <InputLabel>Currency</InputLabel>
                        <Select
                            value={currency}
                            label="Currency"
                            onChange={(e) =>
                                setFormData((p) => ({ ...p, currency: e.target.value }))
                            }
                        >
                            {currencies.map((c) => (
                                <MenuItem key={c.value} value={c.value}>
                                    {c.label}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Grid>

                {/* MOQ */}
                <Grid item xs={3}>
                    <TextField
                        label={<span>MOQ <span className="text-danger">*</span></span>}
                        type="number"
                        fullWidth
                        value={moq}
                        onChange={(e) =>
                            setFormData((p) => ({ ...p, moq: e.target.value }))
                        }
                    />
                </Grid>

                {/* MOQ UNIT */}
                <Grid item xs={3}>
                    <FormControl fullWidth>
                        <InputLabel>Unit</InputLabel>
                        <Select
                            value={moqUnit}
                            label="Unit"
                            onChange={(e) =>
                                setFormData((p) => ({ ...p, moqUnit: e.target.value }))
                            }
                        >
                            {["kg", "g", "mg", "mcg"].map((u) => (
                                <MenuItem key={u} value={u}>
                                    {u}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Grid>

                <div className="w-100">
                    {/* TIER TOGGLE */}
                    <Grid item xs={12}>
                        <Typography>
                            Enable Tiered Pricing
                            <Switch
                                checked={tieringEnabled}
                                onChange={(e) => toggleTiering(e.target.checked)}
                            />
                        </Typography>
                    </Grid>
                </div>

                {/* TIER CONFIG */}
                {tieringEnabled && (
                    <>
                        <Grid container spacing={2} size={8} xs={6} sx={{ mb: 4 }}>
                            <Grid item xs={6}>
                                <TextField
                                    label="Max Price"
                                    type="number"
                                    fullWidth
                                    disabled
                                    value={maxPrice}
                                />
                            </Grid>

                            <Grid item xs={6}>
                                <TextField
                                    label="Min Price"
                                    type="number"
                                    fullWidth
                                    value={minPrice}
                                    onChange={(e) => {
                                        setIsMinPriceManual(true);
                                        setFormData((p) => ({
                                            ...p,
                                            minPrice: Number(e.target.value),
                                        }));
                                    }}
                                />
                            </Grid>
                        </Grid>

                        {tieredPricing?.map((tier, i) => (
                            <Grid container spacing={2} key={i} alignItems="center">
                                <Grid item xs={3}>
                                    <TextField label="Min Qty" value={tier.min} disabled />
                                </Grid>
                                <Grid item xs={3}>
                                    <TextField
                                        label="Max Qty"
                                        value={tier.max ?? "∞"}
                                        disabled={tier.max === null}
                                        onChange={(e) =>
                                            updateTier(i, "max", Number(e.target.value))
                                        }
                                    />
                                </Grid>
                                <Grid item xs={4}>
                                    <TextField
                                        label="Price"
                                        value={tier.p}
                                        onChange={(e) =>
                                            updateTier(i, "p", Number(e.target.value))
                                        }
                                    />
                                </Grid>
                                <Grid item xs={2}>
                                    {i !== tieredPricing.length - 1 && (
                                        <IconButton onClick={() => removeTier(i)}>
                                            <CloseIcon />
                                        </IconButton>
                                    )}
                                </Grid>
                            </Grid>
                        ))}

                        <Grid container xs={12}>
                            <Button
                                variant="contained"
                                className="shadow-none"
                                onClick={addTier}>
                                + Add Tier
                            </Button>
                        </Grid>
                    </>
                )}
            </Grid>
        </Wrapper>
    );
}

const Wrapper = styled.div``;
